import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { DoctorGroup } from '../../detail/doctorInfo/groupCandidatesByDoctor';
import { formatLocation } from '../../detail/doctorInfo/helpers';
import { ApsSource } from '../../detail/doctorInfo/types';
import { useSaveOrder } from '../hooks';
import { DoctorFormData } from '../types';
import './ConfirmView.scss';
import DoctorForm, { DoctorFormHandles } from './DoctorForm';

const SOURCE_LABELS: Record<ApsSource, string> = {
  Dx: 'DX',
  Rx: 'Rx',
  Disclosure: 'Disclosure',
  PreviouslyOrdered: 'Previously Ordered',
};

interface ConfirmViewProps {
  title: string;
  subtitle?: string;
  applicationId: number;
  taskId: number;
  formData: DoctorFormData;
  group?: DoctorGroup | null;
  onBack: () => void;
  onCancel: () => void;
  onSuccess: () => void;
}

const ConfirmField = ({ label, value }: { label: string; value: string }) => (
  <Box className="confirm-field">
    <Typography className="confirm-label">{label}</Typography>
    <Typography className="confirm-val">{value}</Typography>
  </Box>
);

const ConfirmView = ({
  title,
  subtitle,
  applicationId,
  taskId,
  formData,
  group,
  onBack,
  onCancel,
  onSuccess,
}: ConfirmViewProps) => {
  const [notes, setNotes] = useState(formData.additionalNotes ?? '');
  const [orderError, setOrderError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentFormData, setCurrentFormData] = useState<DoctorFormData>(formData);
  const editFormRef = useRef<DoctorFormHandles>(null);

  const handleApplyEdit = async () => {
    const data = await editFormRef.current?.submit();
    if (!data) return;
    setCurrentFormData(data);
    setIsEditing(false);
  };

  const { placeOrder, isLoading } = useSaveOrder(
    applicationId,
    taskId,
    () => {
      setOrderError('');
      onSuccess();
    },
    (err: any) => {
      const message = err?.message ?? 'Failed to place APS order';
      setOrderError(message);
      console.error('Failed to place APS order', err);
    },
  );

  const handleOrder = () => {
    placeOrder({ ...currentFormData, additionalNotes: notes || null });
  };

  const displayName =
    [currentFormData.firstName, currentFormData.lastName]
      .filter(Boolean)
      .join(' ') ||
    currentFormData.facilityName ||
    group?.name ||
    '-';
  const displaySource = group ? SOURCE_LABELS[group.source] : 'Manual';
  const displaySpec = group?.specialization || '-';
  const displayNpi = group?.npi || '-';
  const displayLocation =
    formatLocation(
      '',
      currentFormData.city,
      currentFormData.state,
      currentFormData.zipCode,
    ) || '-';
  const displayPhone = currentFormData.phoneNumber || '-';
  const displayAddress = currentFormData.streetAddress || '-';

  return (
    <Paper variant="outlined" className="confirm-view">
      <Box className="panel-header">
        <Typography className="panel-title">{title}</Typography>
        {subtitle && (
          <Typography className="panel-subtitle">{subtitle}</Typography>
        )}
      </Box>

      <Box className="back-bar">
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={onBack}
          className="back-link"
        >
          ← Back to doctor list
        </Link>
      </Box>

      <Typography className="confirm-section-label">Selected Doctor</Typography>

      <Paper variant="outlined" className="confirm-doctor">
        <Box className="confirm-doctor-header">
          <Typography className="confirm-doctor-name">{displayName}</Typography>
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {!isEditing && (
              <Link
                component="button"
                type="button"
                underline="always"
                onClick={() => setIsEditing(true)}
                className="change-link"
              >
                Edit
              </Link>
            )}
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={onBack}
              className="change-link"
            >
              Change
            </Link>
          </Box>
        </Box>

        {isEditing ? (
          <Box sx={{ padding: '12px 14px' }}>
            <DoctorForm
              ref={editFormRef}
              doctor={null}
              insufficientEhr={false}
              defaultValues={currentFormData}
              hideNotes
            />
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                mt: 1,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsEditing(false)}
                sx={{
                  color: '#374151',
                  borderColor: '#e5e7eb',
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyEdit}
                sx={{
                  bgcolor: '#111827',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className="confirm-doctor-body">
            <Box className="confirm-field-row">
              <ConfirmField label="Source" value={displaySource} />
              <ConfirmField label="Specialization" value={displaySpec} />
              <ConfirmField label="NPI" value={displayNpi} />
            </Box>
            <Box className="confirm-field-row">
              <ConfirmField label="Location" value={displayLocation} />
              <ConfirmField label="Phone" value={displayPhone} />
              <ConfirmField label="Address" value={displayAddress} />
            </Box>
          </Box>
        )}
      </Paper>

      <Box className="notes-section">
        <Typography className="notes-label">
          Additional Notes{' '}
          <Typography component="span" className="notes-optional">
            (optional)
          </Typography>
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add any notes for this APS order, e.g. specific records needed, time period, or context for the request…"
        />
      </Box>

      {orderError && (
        <Box className="confirm-error">
          <Alert severity="error">{orderError}</Alert>
        </Box>
      )}

      <Box className="order-actions">
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isLoading}
          sx={{
            color: '#374151',
            borderColor: '#e5e7eb',
            fontSize: 12,
            fontWeight: 500,
            py: '6px',
            px: '14px',
            lineHeight: '16px',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleOrder}
          disabled={isLoading}
          sx={{
            bgcolor: '#1d4ed8',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            py: '8px',
            px: '20px',
            lineHeight: '16px',
          }}
        >
          {isLoading ? 'Ordering…' : 'Order APS'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ConfirmView;
